import React, { useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { getUser, confirmEmail } from "../../services/Auth/auth";
import LoginForm from "../../components/Auth/LoginForm";
import { useTheme } from "../../components/ThemeContext";
import logoShield from "../../components/ThreatModeling/images/shield.png";
import {
  Network,
  Boxes,
  Search,
  Globe,
  ShieldCheck,
  Sparkles,
  Activity,
  Lock,
  Users,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import GradientText from "../../components/ui/GradientText";
import DotGrid from "../../components/ui/DotGrid";
import "./LoginPage.css";

const FlowArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const IsometricCubeIcon = () => (
  <svg
    viewBox="0 0 100 100"
    style={{ width: 38, height: 38, filter: "drop-shadow(0 0 8px rgba(99, 102, 241, 0.4))" }}
  >
    <path d="M 50 18 L 78 32 L 50 46 L 22 32 Z" fill="url(#cube-top)" />
    <path d="M 22 32 L 50 46 L 50 74 L 22 60 Z" fill="url(#cube-left)" />
    <path d="M 50 46 L 78 32 L 78 60 L 50 74 Z" fill="url(#cube-right)" />
    <defs>
      <linearGradient id="cube-top" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#818cf8" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
      <linearGradient id="cube-left" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#4f46e5" />
        <stop offset="100%" stopColor="#312e81" />
      </linearGradient>
      <linearGradient id="cube-right" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4338ca" />
        <stop offset="100%" stopColor="#1e1b4b" />
      </linearGradient>
    </defs>
  </svg>
);

/* ===== Editorial-styled components using --mk-* CSS custom properties ===== */

const LoginPageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: var(--mk-paper);
  color: var(--mk-ink);
  font-family: var(--mk-font-body);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const LoginCard = styled.div`
  display: flex;
  width: min(95vw, 1280px);
  height: min(90vh, 760px);
  min-height: 520px;
  border-radius: 0;
  box-shadow: var(--token-shadow-lg);
  overflow: hidden;
  position: relative;
  background: var(--mk-paper);
  border: 1px solid var(--mk-line);

  @media (max-width: 900px) {
    flex-direction: column;
    width: min(92vw, 450px);
    height: auto;
    min-height: unset;
    max-height: 95vh;
    overflow-y: auto;
  }
`;

const LeftSection = styled.div`
  flex: 1.35;
  background: var(--mk-paper-soft);
  display: flex;
  flex-direction: column;
  padding: clamp(16px, 3vh, 32px) clamp(24px, 4vw, 48px);
  color: var(--mk-ink);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-right: 1px solid var(--mk-line);

  @media (max-width: 900px) {
    flex: none;
    height: auto;
    min-height: 520px;
    max-height: unset;
    padding: 32px 24px;
    border-right: none;
    border-bottom: 1px solid var(--mk-line);
  }
`;

const HexGrid = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.35;
  pointer-events: none;
  background-image:
    linear-gradient(
      to right,
      transparent calc(100% - 0.5px),
      var(--mk-line-soft) calc(100% - 0.5px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(100% - 0.5px),
      var(--mk-line-soft) calc(100% - 0.5px)
    );
  background-size:
    calc(100% / 12) 100%,
    100% 32px;
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.15) 50%,
    transparent 100%
  );
  z-index: 0;
`;

const DottedWave = ({ isDark }) => (
  <svg
    width="100%"
    height="100%"
    style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 1 }}
    viewBox="0 0 800 600"
    preserveAspectRatio="none"
  >
    <path
      d="M -50 480 C 180 430, 280 210, 480 160 C 680 110, 780 210, 880 110"
      fill="none"
      stroke="url(#wave-grad)"
      strokeWidth="2"
      strokeDasharray="4 8"
      opacity={0.15}
    />
    <path
      d="M -50 500 C 160 450, 260 230, 460 180 C 660 130, 760 230, 860 130"
      fill="none"
      stroke="url(#wave-grad)"
      strokeWidth="1.5"
      strokeDasharray="2 6"
      opacity={0.08}
    />
    <defs>
      <linearGradient id="wave-grad" x1="0" y1="1" x2="1" y2="0">
        <stop offset="0%" stopColor="var(--mk-accent)" />
        <stop offset="50%" stopColor="var(--mk-accent)" />
        <stop offset="100%" stopColor="var(--mk-accent)" />
      </linearGradient>
    </defs>
  </svg>
);

const GlowPurple = styled.div`
  position: absolute;
  top: -100px;
  left: -50px;
  width: 450px;
  height: 450px;
  background: radial-gradient(circle, rgba(91, 43, 217, 0.08) 0%, rgba(91, 43, 217, 0) 70%);
  filter: blur(55px);
  pointer-events: none;
  z-index: 2;
`;

const GlowBlue = styled.div`
  position: absolute;
  bottom: -150px;
  right: -50px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(91, 43, 217, 0.05) 0%, rgba(91, 43, 217, 0) 70%);
  filter: blur(65px);
  pointer-events: none;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 10;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: clamp(16px, 2.5vh, 28px);
`;

const LogoImg = styled.img`
  height: clamp(26px, 2.5vw, 34px);
  width: auto;
  margin-right: 12px;
`;

const LogoText = styled.div`
  font-family: "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-weight: 700;
  font-size: clamp(16px, 1.8vw, 20px);
  line-height: 1.1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--mk-ink);

  span {
    color: var(--mk-accent);
    font-weight: 700;
  }
`;

const Heading = styled.h1`
  font-family: "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-weight: 800;
  font-size: clamp(2.2rem, 5vw + 0.5rem, 4.5rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--mk-ink);
  margin: 0 0 12px 0;

  span.gradient {
    color: var(--mk-accent);
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
  }
`;

const DescriptionText = styled.p`
  font-family: "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-weight: 400;
  font-size: clamp(1rem, 1.2vw + 0.5rem, 1.35rem);
  line-height: 1.45;
  color: var(--mk-ink-body);
  max-width: 56ch;
  margin: 0 0 clamp(16px, 2.5vh, 24px) 0;
`;

const FlowSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: clamp(16px, 2.5vh, 24px);
  gap: 8px;

  @media (max-width: 1100px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
`;

const StepCard = styled.div`
  flex: 1;
  min-width: 105px;
  min-height: 124px;
  background: var(--mk-paper);
  border: 1px solid var(--mk-line);
  border-radius: 0;
  padding: 24px 8px 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: var(--mk-accent);
    background: var(--mk-paper-soft);
    transform: translateY(-4px);
  }

  @media (max-width: 1100px) {
    flex: unset;
    width: calc(50% - 10px);
    min-width: 140px;
    min-height: 130px;
    padding: 28px 12px 20px 12px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const StepBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--mk-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mk-paper);
  font-family: var(--mk-font-mono);
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 0 10px rgba(91, 43, 217, 0.5);
  border: 1.5px solid var(--mk-paper);
`;

const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  margin-bottom: 12px;
`;

const StepText = styled.span`
  font-family: var(--mk-font-body);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--mk-ink-muted);
  text-align: center;
  display: block;
`;

const FlowArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mk-accent);
  opacity: 0.6;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  align-items: center;
  margin-bottom: clamp(16px, 2.5vh, 24px);
`;

const TagsRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
`;

const TagPill = styled.div`
  background: var(--mk-paper);
  border: 1px solid var(--mk-line);
  border-radius: 0;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  color: var(--mk-ink-body);
  font-family: var(--mk-font-mono);
  font-size: 12px;
  font-weight: 500;
  box-shadow: none;
  transition: all 0.2s ease-in-out;

  svg {
    width: 14px;
    height: 14px;
    margin-right: 8px;
    color: ${(props) => props.iconColor || "var(--mk-accent)"};
  }

  &:hover {
    background: var(--mk-paper-soft);
    border-color: var(--mk-accent);
    transform: translateY(-2px) scale(1.02);
  }
`;

const TrustSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: auto;
  padding-top: clamp(12px, 2vh, 18px);
  border-top: 1px solid var(--mk-line);
  gap: clamp(16px, 3vw, 40px);
  z-index: 10;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--mk-font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--mk-ink-muted);
  line-height: 1.35;
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
    color: var(--mk-accent);
    flex-shrink: 0;
  }

  @media (max-width: 900px) {
    max-width: 100%;
    width: calc(50% - 8px);
    justify-content: center;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const RightSection = styled.div`
  flex: 0.8;
  background: var(--mk-paper);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: none;
    min-height: 400px;
  }
`;

const FormScaleWrapper = styled.div`
  width: 420px;
  max-width: 90%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center center;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 340px;
    padding: 20px 0;
  }
`;


const LoginPageInternal = ({ setAuthUser }) => {
  const { isDark } = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [confirmationStatus, setConfirmationStatus] = React.useState(null);

  const isSignUpMode =
    location.pathname === "/signup" ||
    location.pathname === "/register" ||
    searchParams.get("mode") === "signup" ||
    searchParams.get("mode") === "signUp" ||
    searchParams.get("signup") === "true";
  const initialMode = isSignUpMode ? "signUp" : "signIn";

  const checkAuthStatus = useCallback(async () => {
    try {
      const user = await getUser();
      if (user) {
        setAuthUser();
      }
    } catch {
      // Ignore auth fetch errors on landing page.
    }
  }, [setAuthUser]);

  useEffect(() => {
    const tokenHash = searchParams.get("token_hash");
    const type = searchParams.get("type");

    if (tokenHash && type) {
      confirmEmail({ tokenHash, type })
        .then((result) => {
          if (result.isSignedIn) {
            setAuthUser();
          } else {
            setConfirmationStatus("success");
          }
        })
        .catch(() => {
          setConfirmationStatus("error");
        });
    } else {
      checkAuthStatus();
    }
  }, [searchParams, checkAuthStatus, setAuthUser]);

  const handleSignInSuccess = () => {
    checkAuthStatus();
  };

  return (
    <LoginPageContainer className="mk">
      <LoginCard>
        <LeftSection>
          <HexGrid />
          <DotGrid opacity={0.18} />
          <DottedWave isDark={isDark} />
          <GlowPurple />
          <GlowBlue />
          <ContentWrapper>
            <LogoContainer>
              <LogoImg src={logoShield} alt="SBM ThreatForge Logo" />
              <LogoText>
                SBM <span>THREATFORGE</span>
              </LogoText>
            </LogoContainer>

            <Heading>
              <span>Threat Modeling,</span>
              <br />
              <span className="gradient" style={{ color: "var(--mk-accent)" }}>
                Reimagined.
              </span>
            </Heading>

            <DescriptionText>
              SBM ThreatForge helps security and engineering teams identify, assess, and mitigate
              threats with confidence.
            </DescriptionText>

            <FlowSection>
              <StepCard>
                <StepBadge>1</StepBadge>
                <StepIconWrapper>
                  <IsometricCubeIcon />
                </StepIconWrapper>
                <StepText>What are we building?</StepText>
              </StepCard>

              <FlowArrow>
                <FlowArrowIcon />
              </FlowArrow>

              <StepCard>
                <StepBadge>2</StepBadge>
                <StepIconWrapper>
                  <AlertTriangle
                    size={36}
                    color="var(--mk-accent)"
                    style={{ filter: "drop-shadow(0 0 8px rgba(91, 43, 217, 0.4))" }}
                  />
                </StepIconWrapper>
                <StepText>What can go wrong?</StepText>
              </StepCard>

              <FlowArrow>
                <FlowArrowIcon />
              </FlowArrow>

              <StepCard>
                <StepBadge>3</StepBadge>
                <StepIconWrapper>
                  <ShieldCheck
                    size={36}
                    color="var(--mk-accent)"
                    style={{ filter: "drop-shadow(0 0 8px rgba(91, 43, 217, 0.4))" }}
                  />
                </StepIconWrapper>
                <StepText>What should we do about it?</StepText>
              </StepCard>

              <FlowArrow>
                <FlowArrowIcon />
              </FlowArrow>

              <StepCard>
                <StepBadge>4</StepBadge>
                <StepIconWrapper>
                  <CheckCircle2
                    size={36}
                    color="var(--mk-accent)"
                    style={{ filter: "drop-shadow(0 0 8px rgba(91, 43, 217, 0.4))" }}
                  />
                </StepIconWrapper>
                <StepText>Did we do a good job?</StepText>
              </StepCard>
            </FlowSection>

            <TagsContainer>
              <TagsRow>
                <TagPill iconColor="var(--mk-accent)">
                  <Network /> Mission Decomposition
                </TagPill>
                <TagPill iconColor="var(--mk-accent)">
                  <Boxes /> System Decomposition
                </TagPill>
                <TagPill iconColor="var(--mk-accent)">
                  <Search /> Vulnerability Identification
                </TagPill>
              </TagsRow>
              <TagsRow>
                <TagPill iconColor="var(--mk-accent)">
                  <Globe /> Cyber Threat Intelligence
                </TagPill>
                <TagPill iconColor="var(--mk-accent)">
                  <ShieldCheck /> Defense &amp; Risk Analysis
                </TagPill>
                <TagPill iconColor="var(--mk-accent)">
                  <Sparkles /> Mitigation &amp; Remediation
                </TagPill>
              </TagsRow>
              <TagsRow>
                <TagPill iconColor="var(--mk-accent)">
                  <Activity /> Monitoring Analysis &amp; Evaluation
                </TagPill>
              </TagsRow>
            </TagsContainer>

            <TrustSection>
              <TrustItem>
                <ShieldCheck />
                <span>Trusted by security professionals</span>
              </TrustItem>
              <TrustItem>
                <Lock />
                <span>Enterprise-grade security</span>
              </TrustItem>
              <TrustItem>
                <Users />
                <span>Built for cross-functional collaboration</span>
              </TrustItem>
            </TrustSection>
          </ContentWrapper>
        </LeftSection>

        <RightSection>
          <FormScaleWrapper>
            {confirmationStatus === "success" ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <h2 style={{ color: "var(--mk-ink)", marginBottom: "16px" }}>Email Confirmed</h2>
                <p style={{ color: "var(--mk-ink-muted)", marginBottom: "24px" }}>
                  Your account has been verified. You can now sign in.
                </p>
                <LoginForm initialMode="signIn" onSignInSuccess={handleSignInSuccess} />
              </div>
            ) : confirmationStatus === "error" ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <h2 style={{ color: "var(--mk-ink)", marginBottom: "16px" }}>
                  Confirmation Failed
                </h2>
                <p style={{ color: "var(--mk-ink-muted)", marginBottom: "24px" }}>
                  The confirmation link is invalid or has expired. Please try signing up again.
                </p>
                <LoginForm initialMode="signUp" onSignInSuccess={handleSignInSuccess} />
              </div>
            ) : (
              <LoginForm initialMode={initialMode} onSignInSuccess={handleSignInSuccess} />
            )}
          </FormScaleWrapper>
        </RightSection>
      </LoginCard>
    </LoginPageContainer>
  );
};

export default LoginPageInternal;
